import {
  formatEABFieldRow,
  formatEABValue,
  getEABFieldRows as getEABEventFieldRows,
  getEABVisibleFields,
  collectEABLookupIds,
  createEABFieldDisplays,
  resolveEABRelation,
  formatEABEnumValue,
  type EABDisplayFieldRow,
  type EABFieldDisplay as EABEventFieldDisplay,
} from "@fi-solutions/lisa-shared";
export {
  parseEABFieldRecordValue,
  getEABFieldRows as getEABEventFieldRows,
  type EABDisplayFieldRow,
  type EABFieldDisplay as EABEventFieldDisplay,
} from "@fi-solutions/lisa-shared";
import {
  useGetCostCentersForRainbowTileQuery,
  useGetUserForRevisionListQuery,
  useHLPCategoriesQuery,
  useQAErrorCataloguesQuery,
  useRevisionTechnicalPlacesQuery,
  type EABLoadSiteEventsQuery,
} from "@/graphql";
import { computed, unref, type MaybeRef } from "vue";
import { DateTime } from "luxon";
import { useTranslation } from "@/language/translation";
import { useSharedEABTranslations } from "@/modules/sqdc/helper/useSharedEABTranslations";
import { hasConfiguredAccessRight } from "@/functions/accessRightHelper";
import { formatDate } from "@/modules/lisaChart/helper/ChartTileHelper";
import { formatSQDCButtonModuleValue } from "@/modules/sqdc/helper/SQDCButtonModuleHelper";
import { formatSQDCRepairWindowEnumValue } from "@/modules/sqdc/helper/SQDCRepairWindowHelper";
import type { EABEventTypeType } from "./EventActionHelper";
import { localizedIshikawaAreaLabel, normalizeIshikawaConfig } from "./modals/ishikawaConfig";

type SiteDisplayEvent = EABLoadSiteEventsQuery["SQDCEvents"][number];
export type EABDisplayEvent = Pick<SiteDisplayEvent, "id" | "typeId"> &
  Partial<Omit<SiteDisplayEvent, "id" | "typeId">>;
export type EABDisplayField = EABEventTypeType["SQDCEventActionTypeFields"][number];
export function getEABVisibleEventFields(
  event: EABDisplayEvent,
  eventType: EABEventTypeType | null | undefined,
) {
  return getEABVisibleFields(event, eventType?.SQDCEventActionTypeFields ?? []);
}

export function useEABEventFieldLookups(events: MaybeRef<EABDisplayEvent[]>) {
  const lookupIds = computed(() => collectEABLookupIds(unref(events)));
  const userIds = computed(() => lookupIds.value.userIds);
  const costCenterIds = computed(() => lookupIds.value.costCenterIds);
  const technicalPlaceIds = computed(() => lookupIds.value.technicalPlaceIds);
  const errorCatalogueIds = computed(() => lookupIds.value.errorCatalogueIds);

  const { result: usersResult } = useGetUserForRevisionListQuery(
    () => ({ ids: userIds.value }),
    () => ({ enabled: userIds.value.length > 0, fetchPolicy: "cache-first" }),
  );
  const { result: costCentersResult } = useGetCostCentersForRainbowTileQuery(
    () => ({ ids: costCenterIds.value }),
    () => ({ enabled: costCenterIds.value.length > 0, fetchPolicy: "cache-first" }),
  );
  const { result: technicalPlacesResult } = useRevisionTechnicalPlacesQuery(
    () => ({ ids: technicalPlaceIds.value }),
    () => ({ enabled: technicalPlaceIds.value.length > 0, fetchPolicy: "cache-first" }),
  );
  const { result: ideaCategoriesResult } = useHLPCategoriesQuery();
  const { result: errorCataloguesResult } = useQAErrorCataloguesQuery(
    () => ({ ids: errorCatalogueIds.value }),
    () => ({ enabled: errorCatalogueIds.value.length > 0, fetchPolicy: "cache-first" }),
  );

  return {
    usersResult,
    costCentersResult,
    technicalPlacesResult,
    ideaCategoriesResult,
    errorCataloguesResult,
  };
}

export function useEABEventFieldDisplays(events: MaybeRef<EABDisplayEvent[]>) {
  const { t, locale } = useTranslation();
  const { t: sharedT, buttonLabel } = useSharedEABTranslations();
  const lookups = useEABEventFieldLookups(events);

  function getDisplays(
    event: EABDisplayEvent,
    eventType: EABEventTypeType | null | undefined,
    siteId?: number,
  ): EABEventFieldDisplay[] {
    const fields = eventType?.SQDCEventActionTypeFields ?? [];
    const displays = createEABFieldDisplays(event, fields, {
      hasAccess: (field) => hasConfiguredAccessRight(field.visibleForAdminOnly, siteId),
      label: (field) => t(`dynamic.${field.nameId}`),
      formatRows,
      buttonLabel,
      noAccess: t("components.AccessRightWrapper.message"),
    });
    return restoreEABIshikawaAreas(displays, fields, locale.value, t);
  }

  function getFieldValue(event: EABDisplayEvent, field: EABDisplayField) {
    return formatRows(field, getEABEventFieldRows(event, field));
  }

  function formatRows(field: EABDisplayField, rows: EABDisplayFieldRow[]) {
    return formatValue(rows.map((row) => formatFieldRow(field, row)));
  }

  function resolveRelation(relation: string | null | undefined, value: unknown) {
    return resolveEABRelation(
      relation,
      value,
      {
        users: lookups.usersResult.value?.Users,
        costCenters: lookups.costCentersResult.value?.CostCenters,
        technicalPlaces: lookups.technicalPlacesResult.value?.OGTechnicalPlaces,
        ideaCategories: lookups.ideaCategoriesResult.value?.HLPCategories,
        errorCatalogues: lookups.errorCataloguesResult.value?.QAErrorCatalogues,
      },
      (nameId) => t(`dynamic.${nameId}`),
    );
  }
  function formatFieldRow(field: EABDisplayField, row: EABDisplayFieldRow): unknown {
    return formatEABFieldRow(field, row, {
      enumValue: formatEnum,
      repairWindowEnum: (configuredField, value) =>
        formatSQDCRepairWindowEnumValue(configuredField.EnumValues, value, locale.value, t),
      date: (configuredField, value) =>
        typeof value === "string"
          ? formatDate(DateTime.fromISO(value), configuredField.dateGranularity, t)
          : value,
      outageDate: (value) => {
        const date = DateTime.fromISO(value);
        return date.isValid ? date.toLocaleString(DateTime.DATETIME_SHORT) : undefined;
      },
      relation: resolveRelation,
      button: (_type, value) => formatSQDCButtonModuleValue(field.type, value, buttonLabel),
      completionChecks: (count, total) =>
        sharedT(
          "modules.sqdc.components.eventActionBoard.modals.SQDCRevisionModal.completionChecks",
          {
            count,
            total,
          },
        ),
      causeCount: (count) =>
        sharedT("modules.sqdc.components.eventActionBoard.modals.SQDCIshikawaField.causeCount", {
          count,
        }),
      outage: sharedT("modules.sqdc.components.eventActionBoard.modals.SQDCOutageField.outage"),
      noOutage: sharedT("modules.sqdc.components.eventActionBoard.modals.SQDCOutageField.noOutage"),
    });
  }

  function formatEnum(field: EABDisplayField, value: unknown) {
    return formatEABEnumValue(field.EnumValues, value, (nameId) => t(`dynamic.${nameId}`));
  }

  function formatValue(value: unknown): string {
    return formatEABValue(value, sharedT("global.yes"), sharedT("global.no"));
  }

  return { getDisplays, getFieldValue };
}

export function restoreEABIshikawaAreas(
  displays: EABEventFieldDisplay[],
  fields: EABDisplayField[],
  locale: string,
  translate: (key: string) => unknown,
): EABEventFieldDisplay[] {
  const fieldsById = new Map(fields.map((field) => [field.id, field]));
  return displays.map((display) => {
    if (display.ishikawaDiagram == null) return display;
    const field = fieldsById.get(display.key);
    if (field == null) return display;
    const areas = normalizeIshikawaConfig(field.ishikawaConfig, translate).areas;
    return {
      ...display,
      ishikawaDiagram: {
        ...display.ishikawaDiagram,
        areas: areas.map((area) => ({
          ...area,
          label: localizedIshikawaAreaLabel(area, locale),
        })),
      },
    };
  });
}
